import React, { useEffect, useState, useCallback } from "react";
import { X, CheckCircle2, XCircle, AlertCircle, MinusCircle, Loader2, ChevronLeft, ChevronRight, Database } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import baseApi from "utils/axios-instance";

interface VerificationValidation {
  status: "met" | "not_met" | "unclear";
  recorded_value: string | null;
  transcript_value: string | null;
  evidence: string | null;
  reasoning: string | null;
}

interface VerificationItem {
  name: string;
  verification_question_text?: string;
  description?: string;
  status: "met" | "not_met" | "unclear" | "not_applicable";
  evidence: string | null;
  reasoning: string | null;
  validation: VerificationValidation | null;
}

interface VerificationData {
  id: string;
  inquiry_id: string;
  call_log_id: string;
  verification_items: VerificationItem[];
  verification_results_dict: {
    total: number;
    met: number;
    not_met: number;
    unclear: number;
    not_applicable: number;
  };
  creation_ts: string | null;
}

interface VerificationWindowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiryId: string;
}

const VerificationWindow: React.FC<VerificationWindowProps> = ({
  open,
  onOpenChange,
  inquiryId,
}) => {
  const [allVerifications, setAllVerifications] = useState<VerificationData[]>([]);
  const [selectedVerificationIndex, setSelectedVerificationIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null>(null);

  const fetchVerificationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await baseApi.get(`/dashboard/data/transcript-verification/${inquiryId}`);
      if (response.data.verifications && response.data.verifications.length > 0) {
        setAllVerifications(response.data.verifications);
        setSelectedVerificationIndex(0);
      } else {
        setAllVerifications([]);
        setError("No verification data found for this inquiry");
      }
    } catch (err: any) {
      console.error("Error fetching verification data:", err);
      setError(err.response?.data?.detail || "Failed to load verification data");
      setAllVerifications([]);
    } finally {
      setLoading(false);
    }
  }, [inquiryId]);

  useEffect(() => {
    if (open && inquiryId) {
      fetchVerificationData();
      setFilter(null);
      setSelectedVerificationIndex(0);
    }
  }, [open, inquiryId, fetchVerificationData]);

  const selectedVerification = allVerifications[selectedVerificationIndex] || null;

  const handlePreviousVerification = () => {
    if (selectedVerificationIndex > 0) {
      setSelectedVerificationIndex(selectedVerificationIndex - 1);
      setFilter(null);
    }
  };

  const handleNextVerification = () => {
    if (selectedVerificationIndex < allVerifications.length - 1) {
      setSelectedVerificationIndex(selectedVerificationIndex + 1);
      setFilter(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "met":      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "not_met":  return <XCircle className="h-5 w-5 text-red-600" />;
      case "unclear":  return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case "not_applicable": return <MinusCircle className="h-5 w-5 text-gray-400" />;
      default: return null;
    }
  };

  const getValidationIcon = (status: string) => {
    switch (status) {
      case "met":     return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
      case "not_met": return <XCircle className="h-4 w-4 text-red-600 shrink-0" />;
      case "unclear": return <AlertCircle className="h-4 w-4 text-yellow-600 shrink-0" />;
      default: return null;
    }
  };

  const toTitleCase = (text: string): string =>
    text
      .split(/[\s_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  const formatItemName = (name: string): string => toTitleCase(name.replace(/_/g, " "));

  const isCustomQuestion = (item: VerificationItem): boolean =>
    item.name.startsWith("custom_");

  const isCallScriptQuestion = (item: VerificationItem): boolean =>
    item.name.startsWith("call_script_");

  const getItemQuestionText = (item: VerificationItem): string =>
    item.verification_question_text ?? item.description ?? "";

  const getItemDisplayTitle = (item: VerificationItem): string => {
    if (isCustomQuestion(item)) return `[CUSTOM] ${toTitleCase(getItemQuestionText(item))}`;
    if (isCallScriptQuestion(item)) return getItemQuestionText(item) || formatItemName(item.name);
    return formatItemName(item.name);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      met:            "bg-green-100 text-green-800 border-green-300",
      not_met:        "bg-red-100 text-red-800 border-red-300",
      unclear:        "bg-yellow-100 text-yellow-800 border-yellow-300",
      not_applicable: "bg-gray-100 text-gray-800 border-gray-300",
    };
    const variants: Record<string, "default" | "destructive" | "secondary" | "outline"> = {
      met: "default", not_met: "destructive", unclear: "outline", not_applicable: "secondary",
    };
    return (
      <Badge className={colors[status] || ""} variant={variants[status] || "outline"}>
        {status.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const getValidationBadge = (status: string) => {
    const colors: Record<string, string> = {
      met:     "bg-green-50 text-green-700 border-green-200",
      not_met: "bg-red-50 text-red-700 border-red-200",
      unclear: "bg-yellow-50 text-yellow-700 border-yellow-200",
    };
    const labels: Record<string, string> = {
      met: "Value Match", not_met: "Value Mismatch", unclear: "Value Unclear",
    };
    return (
      <Badge className={`text-xs ${colors[status] || ""}`} variant="outline">
        {labels[status] || status}
      </Badge>
    );
  };

  const countValidationMismatches = (items: VerificationItem[]): number =>
    items.filter((i) => i.validation?.status === "not_met").length;

  const toDisplayString = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return JSON.stringify(value, null, 2);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-[98vw] !w-[98vw] max-h-[90vh] overflow-y-auto overflow-x-hidden !fixed !bottom-0 !left-[50%] !translate-x-[-50%] !translate-y-0 !top-auto rounded-t-lg rounded-b-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:translate-y-full data-[state=open]:translate-y-0 transition-transform duration-300"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center justify-between">
            <span>Transcript Verification</span>
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-5 w-7" />
            </button>
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#005151]" />
            <span className="ml-3 text-gray-600">Loading verification data...</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && !error && selectedVerification && allVerifications.length > 0 && (() => {
          const filteredItems = filter === null
            ? selectedVerification.verification_items
            : filter === "value_mismatch"
              ? selectedVerification.verification_items.filter((i) => i.validation?.status === "not_met")
              : selectedVerification.verification_items.filter((item) => item.status === filter);

          const validationMismatches = countValidationMismatches(selectedVerification.verification_items);

          return (
            <div className="space-y-6 w-full overflow-x-hidden">
              {/* Verification Selector */}
              {allVerifications.length > 1 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700">Call Verification:</span>
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={handlePreviousVerification}
                        disabled={selectedVerificationIndex === 0}
                        variant="outline"
                        size="sm"
                        className="h-8"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-semibold text-gray-900 min-w-[120px] text-center">
                        Call {selectedVerificationIndex + 1} of {allVerifications.length}
                      </span>
                      <Button
                        onClick={handleNextVerification}
                        disabled={selectedVerificationIndex === allVerifications.length - 1}
                        variant="outline"
                        size="sm"
                        className="h-8"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    {selectedVerification.creation_ts && (
                      <span className="text-xs text-gray-500">
                        {new Date(selectedVerification.creation_ts).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Summary Section */}
              <div className="bg-gray-50 p-4 rounded-lg w-full">
                <h3 className="text-lg font-semibold mb-3">Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 w-full">
                  <button
                    onClick={() => setFilter(null)}
                    className={`text-center p-3 rounded-lg transition-all cursor-pointer hover:bg-gray-100 ${
                      filter === null ? "bg-white shadow-md ring-2 ring-gray-400" : ""
                    }`}
                  >
                    <div className={`text-2xl font-bold ${filter === null ? "text-gray-900" : "text-gray-700"}`}>
                      {selectedVerification.verification_results_dict.total}
                    </div>
                    <div className="text-sm text-gray-600">Total</div>
                  </button>
                  <button
                    onClick={() => setFilter("met")}
                    className={`text-center p-3 rounded-lg transition-all cursor-pointer hover:bg-green-50 ${
                      filter === "met" ? "bg-green-100 shadow-md ring-2 ring-green-400" : ""
                    }`}
                  >
                    <div className={`text-2xl font-bold ${filter === "met" ? "text-green-700" : "text-green-600"}`}>
                      {selectedVerification.verification_results_dict.met}
                    </div>
                    <div className="text-sm text-gray-600">Met</div>
                  </button>
                  <button
                    onClick={() => setFilter("not_met")}
                    className={`text-center p-3 rounded-lg transition-all cursor-pointer hover:bg-red-50 ${
                      filter === "not_met" ? "bg-red-100 shadow-md ring-2 ring-red-400" : ""
                    }`}
                  >
                    <div className={`text-2xl font-bold ${filter === "not_met" ? "text-red-700" : "text-red-600"}`}>
                      {selectedVerification.verification_results_dict.not_met}
                    </div>
                    <div className="text-sm text-gray-600">Not Met</div>
                  </button>
                  <button
                    onClick={() => setFilter("unclear")}
                    className={`text-center p-3 rounded-lg transition-all cursor-pointer hover:bg-yellow-50 ${
                      filter === "unclear" ? "bg-yellow-100 shadow-md ring-2 ring-yellow-400" : ""
                    }`}
                  >
                    <div className={`text-2xl font-bold ${filter === "unclear" ? "text-yellow-700" : "text-yellow-600"}`}>
                      {selectedVerification.verification_results_dict.unclear}
                    </div>
                    <div className="text-sm text-gray-600">Unclear</div>
                  </button>
                  <button
                    onClick={() => setFilter("not_applicable")}
                    className={`text-center p-3 rounded-lg transition-all cursor-pointer hover:bg-gray-100 ${
                      filter === "not_applicable" ? "bg-gray-200 shadow-md ring-2 ring-gray-400" : ""
                    }`}
                  >
                    <div className={`text-2xl font-bold ${filter === "not_applicable" ? "text-gray-700" : "text-gray-600"}`}>
                      {selectedVerification.verification_results_dict.not_applicable}
                    </div>
                    <div className="text-sm text-gray-600">N/A</div>
                  </button>
                  {/* Value mismatches — only shown when any validation data exists */}
                  {validationMismatches > 0 && (
                    <button
                      onClick={() => setFilter("value_mismatch")}
                      className={`text-center p-3 rounded-lg transition-all cursor-pointer hover:bg-orange-50 ${
                        filter === "value_mismatch" ? "bg-orange-100 shadow-md ring-2 ring-orange-400" : ""
                      }`}
                    >
                      <div className={`text-2xl font-bold ${filter === "value_mismatch" ? "text-orange-700" : "text-orange-600"}`}>
                        {validationMismatches}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
                        <Database className="h-3 w-3" />
                        Data Errors
                      </div>
                    </button>
                  )}
                </div>
                {filter && (
                  <div className="mt-3 text-sm text-gray-600">
                    Showing {filteredItems.length} of {selectedVerification.verification_results_dict.total} items
                    <button
                      onClick={() => setFilter(null)}
                      className="ml-2 text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear filter
                    </button>
                  </div>
                )}
                {selectedVerification.creation_ts && (
                  <div className="mt-3 text-sm text-gray-500">
                    Verified: {new Date(selectedVerification.creation_ts).toLocaleString()}
                  </div>
                )}
              </div>

              {/* Items Section */}
              <div className="w-full overflow-x-hidden">
                <h3 className="text-lg font-semibold mb-3">
                  Verification Items {filter && `(${filteredItems.length})`}
                </h3>
                {filteredItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No items match the selected filter.
                  </div>
                ) : (
                  <div className="space-y-4 w-full">
                    {filteredItems.map((item, index) => {
                      const isCustom = isCustomQuestion(item);
                      const isCallScript = isCallScriptQuestion(item);
                      const hasValidation = item.validation != null;
                      const validationMismatch = item.validation?.status === "not_met";

                      return (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 transition-colors w-full overflow-x-hidden ${
                            validationMismatch
                              ? "border-orange-300 bg-orange-50/30 hover:bg-orange-50/50"
                              : isCustom
                                ? "border-l-4 border-l-[#005151] bg-[#005151]/5 hover:bg-[#005151]/10"
                                : "hover:bg-gray-50"
                          }`}
                        >
                          {/* Header row */}
                          <div className="flex items-start justify-between mb-2 w-full">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              {getStatusIcon(item.status)}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1 flex-wrap gap-y-1">
                                  <h4 className="font-semibold text-gray-900 break-words">{getItemDisplayTitle(item)}</h4>
                                  {getStatusBadge(item.status)}
                                  {hasValidation && getValidationBadge(item.validation!.status)}
                                </div>
                                {!isCustom && !isCallScript && (
                                  <p className="text-sm text-gray-600 mb-2 break-words">{getItemQuestionText(item)}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Reasoning */}
                          {item.reasoning && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border-l-4 border-blue-400 w-full overflow-x-hidden">
                              <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                                <span className="font-medium">Reasoning:</span> {item.reasoning}
                              </p>
                            </div>
                          )}

                          {/* Evidence */}
                          {item.evidence && (
                            <div className="mt-2 p-2 bg-gray-50 rounded w-full overflow-x-hidden">
                              <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                                <span className="font-medium">Evidence:</span> {item.evidence}
                              </p>
                            </div>
                          )}

                          {/* Validation sub-section */}
                          {hasValidation && (
                            <div className={`mt-3 p-3 rounded-lg border w-full overflow-x-hidden ${
                              validationMismatch
                                ? "bg-orange-50 border-orange-200"
                                : item.validation!.status === "met"
                                  ? "bg-green-50 border-green-200"
                                  : "bg-yellow-50 border-yellow-200"
                            }`}>
                              <div className="flex items-center gap-2 mb-2">
                                {getValidationIcon(item.validation!.status)}
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                  Value Check
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recorded</span>
                                  {toDisplayString(item.validation!.recorded_value) != null ? (
                                    <span className={`text-sm font-mono break-all whitespace-pre-wrap ${
                                      validationMismatch ? "text-red-700 font-semibold" : "text-gray-800"
                                    }`}>
                                      {toDisplayString(item.validation!.recorded_value)}
                                    </span>
                                  ) : (
                                    <span className="text-sm italic text-gray-400">—</span>
                                  )}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Transcript</span>
                                  {toDisplayString(item.validation!.transcript_value) != null ? (
                                    <span className={`text-sm font-mono break-all whitespace-pre-wrap ${
                                      validationMismatch ? "text-green-700 font-semibold" : "text-gray-800"
                                    }`}>
                                      {toDisplayString(item.validation!.transcript_value)}
                                    </span>
                                  ) : (
                                    <span className="text-sm italic text-gray-400">not mentioned</span>
                                  )}
                                </div>
                              </div>
                              {item.validation!.reasoning && (
                                <p className="text-xs text-gray-600 break-words whitespace-pre-wrap">
                                  {item.validation!.reasoning}
                                </p>
                              )}
                              {item.validation!.evidence && (
                                <p className="text-xs text-gray-500 mt-1 italic break-words whitespace-pre-wrap">
                                  "{item.validation!.evidence}"
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {!loading && !error && allVerifications.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No verification data available for this inquiry.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerificationWindow;
